/**
 * useMetricStateMachine — typed reactive view onto per-metric lifecycle.
 *
 * The W3 store carve renamed `testStates` → `metricStates`; this composable
 * is the consumer-side facade that retires the per-component
 * `isStarted`/`isFinished`/`isActiveOrStarted` helpers (one copy in
 * SpeedtestResults, one in MetricPillCluster, one in dashboard rows).
 *
 * Per-metric state lookups go through `phaseFor` (jitter rides ping); the
 * 3-slot reactive `metricStates` Record is keyed by `PhaseId`. Every
 * predicate returns a `ComputedRef<boolean>` so consumers can pin them in
 * computed graphs without losing reactivity through a function call.
 *
 * `allComplete` reads from `useMetricRegistry().visible` so the dpi
 * variant (download + upload) computes its completion correctly — the
 * pre-T `metrics.every(isFinished)` always read the 4-set even when only
 * 2 metrics were visible, masking the regression.
 */

import { computed, type ComputedRef } from "vue";

import {
    phaseFor,
    type MetricId,
} from "@src/types/speedtest.ts";
import { TestState } from "@features/speedtest/state/index.ts";
import { useSpeedtestStore } from "@features/speedtest/state/index.ts";

import { useMetricRegistry } from "./useMetricRegistry.ts";

export interface UseMetricStateMachine {
    /** Per-metric current `TestState`. Jitter rides ping. */
    state: (id: MetricId) => ComputedRef<TestState>;
    /** Per-metric `state === started` predicate. */
    isStarted: (id: MetricId) => ComputedRef<boolean>;
    /** Per-metric `state === finished` predicate. */
    isFinished: (id: MetricId) => ComputedRef<boolean>;
    /** Per-metric `state ∈ {started, active}` predicate — the active-pill check. */
    isActiveOrStarted: (id: MetricId) => ComputedRef<boolean>;
    /** True when every visible metric has finished. Variant-aware. */
    allComplete: ComputedRef<boolean>;
}

export function useMetricStateMachine(): UseMetricStateMachine {
    const speedtest = useSpeedtestStore();
    const registry = useMetricRegistry();

    function state(id: MetricId): ComputedRef<TestState> {
        return computed(() => speedtest.metricStates[phaseFor(id)]);
    }

    function isStarted(id: MetricId): ComputedRef<boolean> {
        return computed(() => state(id).value === TestState.started);
    }

    function isFinished(id: MetricId): ComputedRef<boolean> {
        return computed(() => state(id).value === TestState.finished);
    }

    function isActiveOrStarted(id: MetricId): ComputedRef<boolean> {
        return computed(() => {
            const s = state(id).value;
            return s === TestState.started || s === TestState.active;
        });
    }

    const allComplete = computed(() =>
        registry.visible.value.every(
            (m) => speedtest.metricStates[phaseFor(m.id)] === TestState.finished,
        ),
    );

    return {
        state,
        isStarted,
        isFinished,
        isActiveOrStarted,
        allComplete,
    };
}
